import {
	ApplicationConfig,
	ErrorHandler,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	provideRouter,
	Router,
	withInMemoryScrolling,
	withRouterConfig,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideTheming } from 'app/core/theming';
import { provideClaimMosaic } from './core/provider/cm.provider';
import { provideAuth0 } from '@auth0/auth0-angular';
import * as Sentry from '@sentry/angular';
import { PwaService } from './core/pwa/pwa.service';
import { PosthogService } from './core/services/posthog.service';
import { environment } from 'environments/environment';
import { isProtectedClaimMosaicApiRequest } from './core/auth/anonymous-api-requests';

export const isSentryEnabled = (): boolean =>
	environment.production && environment.env === 'prod';

export const createAppConfig = (): ApplicationConfig => ({
	providers: [
		provideAnimations(),
		...(isSentryEnabled()
			? [
					{
						provide: ErrorHandler,
						useValue: Sentry.createErrorHandler(),
					},
					{
						provide: Sentry.TraceService,
						deps: [Router],
					},
					provideAppInitializer(() => {
						inject(Sentry.TraceService);
					}),
				]
			: []),
		provideRouter(
			appRoutes,
			withInMemoryScrolling({
				scrollPositionRestoration: 'enabled',
				anchorScrolling: 'enabled',
			}),
			withRouterConfig({
				onSameUrlNavigation: 'reload',
				paramsInheritanceStrategy: 'always',
			})
		),

		// Theming
		provideTheming({
			scheme: 'system',
		}),
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { appearance: 'fill', subscriptSizing: 'dynamic' },
		},

		// Core services
		provideAuth(),
		provideIcons(),
		provideFuse(),

		// Claim Mosaic
		provideClaimMosaic(),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideAppInitializer(() => {
			const pwaService = inject(PwaService);
			pwaService.initialize();
		}),
		provideAppInitializer(() => {
			const posthogService = inject(PosthogService);
			posthogService.initialize();
		}),

		// Auth0
		provideAuth0({
			...environment.auth0,
			cacheLocation: 'localstorage',
			authorizationParams: {
				...environment.auth0.authorizationParams,
				redirect_uri: window.location.origin,
			},
			httpInterceptor: {
				allowedList: [
					{
						uriMatcher: (uri) =>
							isProtectedClaimMosaicApiRequest(
								uri,
								environment.apiUrl
							),
						tokenOptions: {
							authorizationParams: {
								scope: 'openid profile email',
							},
						},
					},
				],
			},
		}),
	],
});
